import { useBuilderContext } from "@Context/game/builder/builder-context";
import MouseEvents from "@Enums/MouseEvents";
import { Box, Slider, styled, useTheme } from "@mui/material";
import "@pixi/events";
import Dot2 from "@Resources/game/builder/dot2.png";
import Dot4 from "@Resources/game/builder/dot4.png";
import { clamp } from "lodash-es";
import { useEffect, useRef, useState } from "react";
import { getSelectedPlayer, updatePlayer } from "./functions/player-functions";
import { Player, Waypoint as WaypointType } from "./models/builder-data-types";

const waypointSize = 24;
const halfWaypointSize = waypointSize / 2;
const timelinePadding = 30;
const doubleTimelinePadding = timelinePadding * 2;
const waypointOffset = timelinePadding - halfWaypointSize;

const CustomSlider = styled(Slider)({
  "& .MuiSlider-thumb": {
    transition: "left 0s",
  },
  "& .MuiSlider-track": {
    transition: "left 0s",
  },
  width: "100%",
  padding: "0",
  margin: "0",
});

interface ITimeline {
  showWaypoints?: boolean;
}

const Timeline = ({ showWaypoints = true }: ITimeline) => {
  const { state, dispatch } = useBuilderContext();
  const timelineRef = useRef<HTMLDivElement>(null);
  const [draggingWaypoint, setDraggingWaypoint] = useState<number>(-1);
  const [dragPosition, setDragPosition] = useState<number>(0);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | undefined>();
  const [timelineWidth, setTimelineWidth] = useState<number>(0);
  const theme = useTheme();

  useEffect(() => {
    setSelectedPlayer(getSelectedPlayer(state));
  }, [state.selectedPlayer, state.clip.scenario.players]);

  useEffect(() => {
    const updateWidth = () => {
      if (timelineRef.current) {
        const container = timelineRef.current.querySelector(".MuiBox-root");
        if (container) {
          setTimelineWidth(container.clientWidth - doubleTimelinePadding);
        }
      }
    };

    // Create a ResizeObserver to watch for size changes
    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    // Start observing the container
    if (timelineRef.current) {
      const container = timelineRef.current.querySelector(".MuiBox-root");
      if (container) {
        resizeObserver.observe(container);
      }
    }

    // Initial update
    updateWidth();

    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const handleWaypointClick = (e: any, index: number) => {
    dispatch({ selectedWaypoint: index });
  };

  const handleWaypointDragStart = (e: any, index: number) => {
    dispatch({ selectedWaypoint: index });
    setDragPosition(selectedPlayer?.points[index]?.t || 0);
    setDraggingWaypoint(index);
  };

  const handleWaypointDragMove = (e: any) => {
    if (draggingWaypoint <= -1) return;
    const viewportRect = timelineRef.current?.getBoundingClientRect() || { left: 0 };
    setDragPosition(
      clamp(((e.clientX - viewportRect.left - waypointOffset) / timelineWidth) * state.clip.scenario.endTime, 0, state.clip.scenario.endTime)
    );
  };

  const handleWaypointDragEnd = () => {
    if (draggingWaypoint <= -1) return;
    const newWaypoints: WaypointType[] = [];
    const selectedPlayer = getSelectedPlayer(state);
    if (!selectedPlayer) {
      setDraggingWaypoint(-1);
      return;
    }
    for (let i = 0; i < selectedPlayer.points.length; i++) {
      newWaypoints.push({ ...selectedPlayer.points[i] });
    }
    const newWaypoint = { ...newWaypoints[draggingWaypoint] };
    newWaypoint.t = dragPosition;
    newWaypoints[draggingWaypoint] = newWaypoint;
    const newPlayer = { ...selectedPlayer, points: newWaypoints };
    updatePlayer(state, dispatch, newPlayer);
    setDraggingWaypoint(-1);
  };

  useEffect(() => {
    window.addEventListener(MouseEvents.MOUSE_UP, handleWaypointDragEnd);
    window.addEventListener(MouseEvents.BLUR, handleWaypointDragEnd);
    return () => {
      window.removeEventListener(MouseEvents.MOUSE_UP, handleWaypointDragEnd);
      window.removeEventListener(MouseEvents.BLUR, handleWaypointDragEnd);
    };
  }, [draggingWaypoint, dragPosition, state]);

  const Ruler = () => {
    const rulerStyle: React.CSSProperties = {
      margin: 0,
      padding: 0,
      listStyle: "none",
      display: "inline-block",
      position: "relative" as const,
      fontFamily: "Inter",
      color: theme.palette.dsNeutral.v400,
      width: "100%",
    };

    const rulerItemBeforeStyle: React.CSSProperties = {
      content: "",
      position: "absolute",
      borderLeft: "1px solid #444",
      height: "15px",
      top: "5px",
      fontFamily: "Inter",
      paddingLeft: "5px",
      marginTop: "-18px",
    };

    // Calculate appropriate tick intervals based on available width
    const minTickWidth = 60; // Minimum pixels between major ticks to ensure readability
    const totalTime = state.clip.scenario.endTime;
    const availableWidth = timelineWidth;

    // Calculate how many major ticks we can fit
    const maxTicks = Math.floor(availableWidth / minTickWidth);
    const tickInterval = Math.ceil(totalTime / maxTicks);

    // Generate major ticks (whole numbers)
    const majorTicks = Array.from({ length: Math.floor(totalTime / tickInterval) + 1 }, (_, i) => i * tickInterval);

    // Generate minor ticks based on the major tick interval
    const minorTickInterval = tickInterval <= 1 ? 0.25 : tickInterval <= 2 ? 0.5 : 1;
    const minorTicks = majorTicks.flatMap(majorTick =>
      Array.from({ length: Math.floor(tickInterval / minorTickInterval) - 1 }, (_, i) => majorTick + (i + 1) * minorTickInterval)
    );

    const mitreWidth = timelineWidth / totalTime;

    return (
      <ul style={rulerStyle}>
        {majorTicks.map(tick => (
          <li
            style={{
              ...rulerItemBeforeStyle,
              left: tick * mitreWidth,
              height: "15px",
              top: "5px",
            }}
            key={`major-${tick}`}
          >
            {tick}
          </li>
        ))}
        {minorTicks.map(tick => (
          <li
            style={{
              ...rulerItemBeforeStyle,
              left: tick * mitreWidth,
              height: "8px",
              top: "17.5px",
              borderLeft: "1px solid #666",
            }}
            key={`minor-${tick}`}
          />
        ))}
      </ul>
    );
  };

  return (
    <Box
      ref={timelineRef}
      sx={{
        position: "relative",
        paddingTop: "20px",
        paddingBottom: "10px",
        width: "100%",
        boxSizing: "border-box",
        minWidth: "300px",
      }}
      onMouseMove={e => handleWaypointDragMove(e)}
    >
      <Box
        className="MuiBox-root"
        sx={{
          width: "100%",
          padding: `0 ${timelinePadding}px`,
          boxSizing: "border-box",
          position: "relative",
          minWidth: "0",
        }}
      >
        <Ruler />
        <CustomSlider
          disabled={state.clip.scenario.endTime <= 0}
          aria-label="Time"
          value={state.time}
          onChange={(e, v) => dispatch({ time: v })}
          min={0}
          max={state.clip.scenario.endTime}
          step={0.01}
          color={selectedPlayer && selectedPlayer.team === 2 ? "error" : "primary"}
        />
      </Box>
      {(state.options.showWaypoints || showWaypoints) &&
        selectedPlayer &&
        selectedPlayer.points.map((point, index) => (
          <Box
            key={index}
            sx={{
              position: "absolute",
              left: waypointOffset + ((index === draggingWaypoint ? dragPosition : point.t) / state.clip.scenario.endTime) * timelineWidth,
              top: 34,
              width: "24px",
              height: "24px",
              background: "url(" + (index === state.selectedWaypoint ? Dot4 : Dot2) + ")",
              backgroundSize: "contain",
            }}
            onClick={e => handleWaypointClick(e, index)}
            onMouseDown={e => handleWaypointDragStart(e, index)}
          />
        ))}
    </Box>
  );
};

export default Timeline;
